import React, { useState, useEffect, useRef } from 'react';
import styles from './FeedbackModal.module.scss';
import { message } from 'antd';
import { FeedbackData, postDraftFeedback } from '~/pages/questionnaires/v2/customer/api/apiMethods';
import cn from 'classnames';
import { CustomButton } from '~/components';
import { sanitizeData, getSentryAnonId } from '~/utils';
import { flushSentryReplay } from '~/containers/sentry/utils';
import { useAnalyticsContext } from '~/services/analytics';

interface FeedbackModalProps {
	isOpen: boolean;
	onClose: () => void;
	questionnaireType: string;
	questionnaireId: number | string;
}

export const FeedbackModal = ({
	isOpen,
	onClose,
	questionnaireType,
	questionnaireId,
}: FeedbackModalProps) => {
	const [rating, setRating] = useState<number>(0);
	const [description, setDescription] = useState<string>('');
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);

	const analytics = useAnalyticsContext();
	analytics?.trackStep('init_modal_feedback');

	const controllerRef = useRef<AbortController | null>(null);

	useEffect(() => {
		document.body.style.overflow = isOpen ? 'hidden' : 'unset';
		return () => {
			document.body.style.overflow = 'unset';
		};
	}, [isOpen]);

	const handleSubmit = async (e: React.FormEvent<HTMLButtonElement>): Promise<void> => {
		e.preventDefault();
		if (isSubmitting) return;

		setIsSubmitting(true);
		setLoading(true);
		controllerRef.current = new AbortController();

		try {
			const sanitizedDescription = sanitizeData(description);


			const feedbackData: FeedbackData = {
				questionnaire_type: questionnaireType,
				questionnaire_id: questionnaireId,
				rating,
				description: sanitizedDescription,
			};

			feedbackData.sentry_id = getSentryAnonId();
			flushSentryReplay();

			const res = await postDraftFeedback({
				body: feedbackData,
				signal: controllerRef.current.signal,
			});

			if (!res?.data?.success) {
				message.error(res?.data?.message || 'Ошибка при отправке отзыва');
				return;
			}

			setIsSubmitted(true);
			setDescription('');
			setRating(0);
		} catch (err) {
			if ((err as any)?.name !== 'AbortError') {
				message.error((err as any)?.response?.data?.message || 'Не удалось отправить отзыв');
			}
		} finally {
			setLoading(false);
			setIsSubmitting(false);
		}
	};

	const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	if (!isOpen) return null;

	return (
		<div className={styles.feedbackModalOverlay} onClick={handleOverlayClick}>
			<div className={styles.feedbackModal}>
				<button
					className={styles.feedbackModalClose}
					onClick={onClose}
					aria-label="Закрыть модальное окно"
				>
					&times;
				</button>

				{!isSubmitted ? (
					<>
						<h2 className={styles.feedbackModalTitle}>Оцените процесс оформления</h2>
						<p className={styles.feedbackModalDescription}>
							Поделитесь вашим мнением, чтобы мы могли улучшить наш сервис
						</p>

						<form className={styles.feedbackForm}>
							<div className={styles.ratingSection}>
								<p>Насколько легко было заполнить анкету?</p>
								<div className={styles.ratingStars}>
									{[1, 2, 3, 4, 5].map((star) => (
										<button
											key={star}
											type="button"
											className={cn(styles.star, {
												[styles.active]: rating >= star,
											})}
											onClick={() => setRating(star)}
											aria-label={`Оценить на ${star} из 5`}
										>
											★
										</button>
									))}
								</div>
								<div className={styles.ratingLabels}>
									<span>Сложно</span>
									<span>Очень легко</span>
								</div>
							</div>

							<div className={styles.commentSection}>
								<label htmlFor="description">Ваши комментарии (необязательно)</label>
								<textarea
									id="description"
									value={sanitizeData(description)}
									onChange={(e) => setDescription(e.target.value)}
									placeholder="Что понравилось или что можно улучшить?"
									maxLength={1000}
								/>
							</div>

							<div className={styles.feedbackModalActions}>
								<CustomButton
									name="Пропустить"
									type="button"
									onClick={onClose}
									className={styles.buttonBack}
									disabled={isSubmitting}
								/>
								<CustomButton
									name="Отправить отзыв"
									type="button"
									disabled={isSubmitting || rating === 0}
									onClick={handleSubmit}
									isLoading={loading}
								/>
							</div>
						</form>
					</>
				) : (
					<div className={styles.feedbackSuccess}>
						<div className={styles.successIcon}>✓</div>
						<h2>Спасибо за ваш отзыв!</h2>
						<p>Ваше мнение поможет нам стать лучше.</p>
					</div>
				)}
			</div>
		</div>
	);
};
